import type { OfferDto } from "@intelligems/ig-types/dtos/offers";
import type {
  ProgressBarTierVariables,
  ProgressBarWidgetConfig,
  ProgressBarWidgetDto,
  ProgressBarWidgetVariant,
  ResolvedProgressBarDeviceConfig,
} from "@intelligems/ig-types/schemas/widgets/progress-bar-widget";
import type { WidgetDeviceType } from "@intelligems/ig-types/schemas/widgets/common";
import { getTextColor } from "@intelligems/ig-utils/common";
import { merge } from "@intelligems/ig-utils/widgets/merge";

export function getDefaultDeviceConfig(
  device: WidgetDeviceType,
  variant: ProgressBarWidgetVariant = "progressBar1",
  offer?: OfferDto
) {
  const tiers = offer?.tiers ?? [];
  const config: ResolvedProgressBarDeviceConfig = {
    device,
    variant,
    styles: {
      container: {
        backgroundColor: "#FFFFFF",
      },
      general: {
        cartTextPosition: "below",
      },
      bar: {
        activeColor: "#E0E0E0",
        inactiveColor: "#F1F1F1",
        thickness: 12,
        backgroundColor: "#FFFFFF",
        cornerRadius: 12,
      },
      layout: {
        swapTextPositions: false,
      },
      textColors: {
        dynamicProgressText: "#000000",
        breakpointTextInactive: "#000000",
        breakpointTextActive: "#000000",
      },
      breakpoint: {
        activeColor: "#E0E0E0",
        inactiveColor: "#F1F1F1",
        activeIconColor: getTextColor("#E0E0E0"),
        inactiveIconColor: getTextColor("#F1F1F1"),
        size: 28,
        iconSize: 12,
        hideText: false,
      },
      typography: {
        dynamicProgressText: {
          fontSize: "14px",
          fontStyle: "medium",
        },
        breakpointText: {
          fontSize: "12px",
          fontStyle: "light",
        },
      },
      tooltip: {
        backgroundColor: "#E1E1E1",
      },
    },
    variables: {
      subtotalQuerySelector: "",
      defaultShippingThreshold: 100,
      textReplacements: {
        ...getDefaultTierVariables(variant),
        unitName: "item",
        more: "more",
        allTiersUnlockedTemplate: "",
        tierVariables: tiers.reduce(
          (variables, tier) => {
            variables[tier.id] = getDefaultTierVariables(variant);
            return variables;
          },
          {} as Record<string, ProgressBarTierVariables>
        ),
      },
    },
  };

  return config;
}

function getDefaultTierVariables(
  variant: ProgressBarWidgetVariant
): ProgressBarTierVariables {
  const variables: ProgressBarTierVariables = {
    buyMoreTemplate: "",
    buyMoreTemplateType: "normal",
    currentUnlockedTemplate: "",
    currentUnlockedTemplateType: "normal",
    tooltipTemplate: "",
    tooltipEnabled: true,
    activeIconId: "check",
    activeBackgroundIconId: "circle",
    inactiveIconId: "lockClosed",
    inactiveBackgroundIconId: "circle",
  };
  switch (variant) {
    case "progressBarWidget1":
      variables.inactiveIconId = "lockClosedVersion1";
      break;
    case "progressBarWidget2":
      variables.inactiveIconId = "lockClosedVersion1";
      break;
    case "progressBarWidget4":
      variables.inactiveIconId = "lockClosedVersion1";
      variables.activeBackgroundIconId = "star";
      variables.inactiveBackgroundIconId = "star";
      break;
  }
  return variables;
}

export function resolveProgressBarDesktopConfig(
  config: ProgressBarWidgetConfig,
  offer?: OfferDto
) {
  return merge(
    getDefaultDeviceConfig("desktop", config.desktop?.variant, offer),
    config.desktop
  );
}

export function resolveProgressBarMobileConfig(
  config: ProgressBarWidgetConfig,
  offer?: OfferDto
) {
  return merge(
    getDefaultDeviceConfig(
      "mobile",
      config.mobile?.variant ?? config.desktop?.variant,
      offer
    ),
    (merger) => {
      merger.merge(config.desktop);
      merger.merge(config.mobile);
    }
  );
}

export function resolveProgressBarWidgetConfig(
  widget: ProgressBarWidgetDto,
  offer?: OfferDto
) {
  return {
    widgetType: widget.widgetType,
    desktop: resolveProgressBarDesktopConfig(widget.config, offer),
    mobile: resolveProgressBarMobileConfig(widget.config, offer),
  };
}
